// Privacy-conscious internal product events.
//
// We record an event name, an optional coarse property bag and (when signed in)
// the account id. No page URLs with query strings, no IP, no third-party
// tracker, no personal details. Failures are swallowed: analytics must never
// break a user action.

import { supabase } from "@/integrations/supabase/client";

export const PRODUCT_EVENTS = [
  "landing_page_view",
  "upgrade_button_clicked",
  "landing_view",
  "page_view",
  "pricing_view",
  "signup_started",
  "signup_completed",
  "login_completed",
  "deal_analysis_started",
  "deal_analysis_completed",
  "free_analysis_1_completed",
  "free_analysis_2_completed",
  "free_analysis_3_completed",
  "free_limit_reached",
  "checkout_completed",
  "subscription_started",
  "subscription_cancelled",
  "subscription_renewed",
  "signup",
  "first_analysis",
  "second_analysis",
  "report_generated",
  "upgrade_clicked",
  "checkout_started",
  "paid",
  "canceled",
  "refunded",
] as const;

export type ProductEvent = (typeof PRODUCT_EVENTS)[number];

/** Coarse, non-identifying detail only (plan id, feature key, counts). */
export type EventProps = Record<string, string | number | boolean | null>;

const sentOncePerSession = new Set<string>();

const ATTRIBUTION_KEY = "arosflow:attribution";

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing_path?: string;
  referrer_host?: string;
  /** Coarse campaign/social origin, derived only from UTM or referrer host. */
  social_source?: string;
}

/** Known social/campaign referrers, matched against the bare referring host. */
const SOCIAL_HOSTS: Record<string, string> = {
  "facebook.com": "facebook",
  "m.facebook.com": "facebook",
  "instagram.com": "instagram",
  "l.instagram.com": "instagram",
  "tiktok.com": "tiktok",
  "youtube.com": "youtube",
  "m.youtube.com": "youtube",
  "linkedin.com": "linkedin",
  "lnkd.in": "linkedin",
  "x.com": "x",
  "twitter.com": "x",
  "t.co": "x",
  "reddit.com": "reddit",
  "google.com": "google",
  "bing.com": "bing",
};

/** Derives a coarse campaign source. UTM wins; otherwise the referring host. */
export function socialSourceFrom(
  utmSource: string | undefined,
  referrerHost: string | undefined,
): string | undefined {
  if (utmSource) return utmSource.toLowerCase().slice(0, 32);
  if (!referrerHost) return undefined;
  const host = referrerHost.toLowerCase().replace(/^www\./, "");
  return SOCIAL_HOSTS[host] ?? host.slice(0, 32);
}

/** Coarse device bucket. No user-agent string, no fingerprinting. */
export function deviceBucket(
  width: number,
  coarsePointer: boolean,
): "mobile" | "tablet" | "desktop" {
  if (width < 640) return "mobile";
  if (width < 1024 || coarsePointer) return "tablet";
  return "desktop";
}

function currentDevice(): "mobile" | "tablet" | "desktop" | undefined {
  if (typeof window === "undefined") return undefined;
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  return deviceBucket(window.innerWidth, coarse);
}

/**
 * Captures first-touch campaign attribution once per browser. Only the
 * campaign parameters, the landing path and the referring host are kept —
 * never full URLs with query strings, and never property or personal data.
 */
export function captureAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  try {
    const stored = window.localStorage.getItem(ATTRIBUTION_KEY);
    if (stored) return JSON.parse(stored) as Attribution;
    const params = new URLSearchParams(window.location.search);
    const attribution: Attribution = {};
    for (const key of ["utm_source", "utm_medium", "utm_campaign"] as const) {
      const v = params.get(key);
      if (v) attribution[key] = v.slice(0, 64);
    }
    attribution.landing_path = window.location.pathname.slice(0, 120);
    if (document.referrer) {
      try {
        attribution.referrer_host = new URL(document.referrer).hostname;
      } catch {
        /* ignore malformed referrers */
      }
    }
    const social = socialSourceFrom(attribution.utm_source, attribution.referrer_host);
    if (social) attribution.social_source = social;
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    return attribution;
  } catch {
    return {};
  }
}

export async function trackEvent(event: ProductEvent, props: EventProps = {}): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const { data } = await supabase.auth.getSession();
    await supabase.from("product_events").insert({
      user_id: data.session?.user?.id ?? null,
      event,
      props: { ...captureAttribution(), device: currentDevice() ?? null, ...props },
    });
  } catch {
    // Never surface analytics failures to the user.
  }
}

/** Fires at most once per browser tab — used for view-style events. */
export function trackOnce(event: ProductEvent, props: EventProps = {}): void {
  const key = `${event}:${JSON.stringify(props)}`;
  if (sentOncePerSession.has(key)) return;
  sentOncePerSession.add(key);
  void trackEvent(event, props);
}

const ANALYSIS_COUNT_KEY = "arosflow:analysis-count";

/**
 * Records an analysis and emits first_analysis / second_analysis milestones.
 * The counter lives in this browser only; it is a milestone marker, not a
 * billing record. Usage that costs money is metered server-side.
 */
export function trackAnalysis(props: EventProps = {}): void {
  if (typeof window === "undefined") return;
  let n = 0;
  try {
    n = Number(window.localStorage.getItem(ANALYSIS_COUNT_KEY) ?? "0") + 1;
    window.localStorage.setItem(ANALYSIS_COUNT_KEY, String(n));
  } catch {
    return;
  }
  if (n === 1) {
    void trackEvent("first_analysis", props);
    void trackEvent("free_analysis_1_completed", props);
  }
  if (n === 2) {
    void trackEvent("second_analysis", props);
    void trackEvent("free_analysis_2_completed", props);
  }
  if (n === 3) void trackEvent("free_analysis_3_completed", props);
  void trackEvent("deal_analysis_completed", { ...props, analysis_number: n });
}
